import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ButtonModule } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';

import { ConfiguracoesService } from '@app/shared/components/api/services/configuracoes.service';
import { EnumService } from '@app/shared/components/api/services/enum.service';

interface ConfiguracaoMeta {
  id: number;
  label: string;
  tipo: 'boolean' | 'int' | 'decimal' | 'select';
  step?: string;
}

const DOACAO_ENUM = 1; // <- ENUM GERAL PARA DOAÇÃO

const DOACAO_META: ConfiguracaoMeta[] = [
  { id: 1, label: 'Financiamento Doação', tipo: 'boolean' },

  { id: 4, label: 'Comissão por campanha (%)', tipo: 'decimal', step: '0.1' },
  { id: 5, label: 'Taxa de serviço (%)', tipo: 'decimal', step: '0.1' },
  { id: 6, label: 'Doação mínima', tipo: 'int' },
  { id: 7, label: 'Doação máxima', tipo: 'int' },
  { id: 8, label: 'Duração mínima', tipo: 'int' },
  { id: 9, label: 'Duração máxima', tipo: 'int' },
  { id: 10, label: 'Qtd. projetos criador', tipo: 'int' },
  { id: 11, label: 'Prazo alerta', tipo: 'int' },

  { id: 12, label: 'Aprovação campanha', tipo: 'select' },

  { id: 13, label: 'Campanha bem sucedida (%)', tipo: 'int' },
  { id: 14, label: 'Limite de doação por campanha', tipo: 'int' },
  { id: 15, label: 'Doação não permitida', tipo: 'int' }
];

@Component({
  selector: 'app-doacao-config',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CheckboxModule,
    SelectModule,
    ToggleSwitchModule,
    ButtonModule,
    InputText,
  ],
  providers: [
    ConfiguracoesService,
    EnumService,
    MessageService
  ],
  templateUrl: './doacao-config.component.html',
  styleUrl: './doacao-config.component.scss'
})
export class DoacaoConfigComponent implements OnInit {

  private readonly fb = inject(FormBuilder);
  private readonly configuracoesService = inject(ConfiguracoesService);
  private readonly enumService = inject(EnumService);
  private readonly messageService = inject(MessageService);

  form!: FormGroup;
  aprovacaoOptions: any[] = [];
  originalValues: Record<number, any> = {};
  booleanFields = DOACAO_META.filter(c => c.tipo === 'boolean');
  numericFields = DOACAO_META.filter(c => c.tipo === 'int' || c.tipo === 'decimal');
  selectFields = DOACAO_META.filter(c => c.tipo === 'select');

  ngOnInit(): void {
    this.initForm();
    this.listenFinanciamentoToggle();
    this.loadConfiguracoes();
    this.loadDropdowns();
  }

  private initForm(): void {
    const controls: Record<string, any> = {};

    DOACAO_META.forEach(cfg => {
      controls[cfg.id.toString()] = [null];
    });

    this.form = this.fb.group(controls);
  }

  private loadDropdowns(): void {
    this.enumService.getAllAprovacaoCampanha()
      .subscribe(res => {
        if (res?.data) {
          this.aprovacaoOptions = Object.entries(res.data)
            .map(([id, label]) => ({ id: Number(id), label }));
        }
      });
  }

  private loadConfiguracoes(): void {
    this.configuracoesService
      .list(DOACAO_ENUM)
      .subscribe(grupo => {

        grupo.configuracaoParametros.forEach(param => {

          this.form
            .get(param.configuracoesEspecificaENUM.toString())
            ?.setValue(param.valor, { emitEvent: false });

          this.originalValues[param.configuracoesEspecificaENUM] = param.valor;
        });

        this.applyFinanciamentoState();
        this.form.markAsPristine();
      });
  }

  private listenFinanciamentoToggle(): void {
    this.financiamentoControl?.valueChanges.subscribe((ativo: boolean) => {

      Object.keys(this.form.controls).forEach(key => {
        if (key === '1') return;

        const control = this.form.get(key);
        if (!control) return;

        ativo ? control.enable({ emitEvent: false })
          : control.disable({ emitEvent: false });
      });
    });
  }

  private applyFinanciamentoState(): void {
    const ativo = this.financiamentoControl?.value;

    Object.keys(this.form.controls).forEach(key => {

      if (key === '1') return; // ignora o checkbox

      const control = this.form.get(key);
      if (!control) return;

      if (ativo) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
    });
  }

  get financiamentoControl() {
    return this.form.get('1');
  }

  save(): void {
    const updates: any[] = [];

    Object.keys(this.form.controls).forEach(key => {
      const enumId = Number(key);
      const newValue = this.form.get(key)?.value;
      const original = this.originalValues[enumId];

      if (newValue === original) return;

      const meta = DOACAO_META.find(c => c.id === enumId);
      if (!meta) return;

      let tipoDadoENUM = 1;
      let valor = String(newValue);

      if (meta.tipo === 'decimal') tipoDadoENUM = 2;
      if (meta.tipo === 'boolean') {
        tipoDadoENUM = 5;
        valor = newValue ? 'true' : 'false';
      }

      updates.push({
        valor,
        tipoDadoENUM,
        configuracoesEspecificaENUM: enumId
      });
    });

    if (!updates.length) return;

    const payload = [{
      configuracoesGeralENUM: DOACAO_ENUM,
      configuracaoParametros: updates
    }];

    this.form.disable();

    this.configuracoesService.updateByTipo(payload)
      .subscribe({
        next: () => {
          updates.forEach(u => {
            this.originalValues[u.configuracoesEspecificaENUM] =
              this.form.get(u.configuracoesEspecificaENUM.toString())?.value;
          });

          this.form.markAsPristine();
          this.form.enable();

          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Configurações de Doação atualizadas',
            life: 3000
          });
        },
        error: () => {
          this.form.enable();
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Falha ao atualizar',
            life: 4000
          });
        }
      });
  }

  trackById(_: number, item: ConfiguracaoMeta) {
    return item.id;
  }

  onToggleBlur(): void {
    const enumId = 1;
    const value = this.financiamentoControl?.value;

    if (value === this.originalValues[enumId]) return;

    const payload = [{
      configuracoesGeralENUM: DOACAO_ENUM,
      configuracaoParametros: [{
        configuracoesEspecificaENUM: enumId,
        valor: value ? 'true' : 'false',
        tipoDadoENUM: 5
      }]
    }];

    this.configuracoesService.updateByTipo(payload)
      .subscribe({
        next: () => {
          this.originalValues[enumId] = value;

          this.messageService.add({
            severity: 'success',
            summary: 'Atualizado',
            detail: value
              ? 'Doação ativada'
              : 'Doação desativada',
            life: 2500
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Falha ao atualizar estado de Doação',
            life: 4000
          });
        }
      });
  }
}